// ERP (SGP / IXC) helpers and constants shared by the integration forms.
import { i18n } from "../translate/i18n";

export const ERP_TYPES = ["SGP", "IXC", "ASAAS"];

// Asaas (plataforma de cobrança): só chave de API + ambiente; a URL é derivada do ambiente.
export const ASAAS_JSON_FIELDS = ["asaasApiKey", "asaasEnvironment", "tipoIntegracao", "timeoutMs"];
export const ASAAS_ENVIRONMENTS = [
  { value: "production", label: "Produção (api.asaas.com)" },
  { value: "sandbox", label: "Sandbox (api-sandbox.asaas.com)" },
];

export const SGP_PASSWORD_OPTIONS = [
  { value: "S", label: "SIM" },
  { value: "N", label: "NÃO" },
];

// SGP access mode (jsonContent.sgpMode).
//   auto    -> uses the URA API whenever sgpApp + sgpToken are filled,
//              otherwise falls back to the subscriber portal (central).
//   central -> always the subscriber portal (CPF + optional password).
//   ura     -> always the URA API (sgpApp + sgpToken required).
// Prefer i18n.t(labelKey) at render time; `label` is a pt fallback.
export const SGP_MODES = [
  { value: "auto", label: "Automático", labelKey: "erp.sgpModes.auto" },
  { value: "central", label: "Somente central do assinante", labelKey: "erp.sgpModes.central" },
  { value: "ura", label: "Somente API URA", labelKey: "erp.sgpModes.ura" },
];
export const SGP_DEFAULT_MODE = "auto";

// Every key the SGP form keeps inside jsonContent.
export const SGP_JSON_FIELDS = [
  "sgpIeSenha",
  "sgpUrl",
  "sgpWidePayToken",
  "tipoIntegracao",
  "sgpApp",
  "sgpToken",
  "sgpMode",
];

// tipoIntegracao options: prefer i18n.t(labelKey) at render time; `label` is a pt fallback
export const ERP_INTEGRATION_TYPES = [
  { value: "NA", label: "Nenhuma", labelKey: "erp.integrationTypes.NA" },
  { value: "SB", label: "2ª Via Boleto", labelKey: "erp.integrationTypes.SB" },
  { value: "AV", label: "A Vencer", labelKey: "erp.integrationTypes.AV" },
  { value: "VE", label: "Vencido", labelKey: "erp.integrationTypes.VE" },
  { value: "LC", label: "Liberação por Confiança", labelKey: "erp.integrationTypes.LC" },
  { value: "ST", label: "Status da Conexão", labelKey: "erp.integrationTypes.ST" },
];

export const ISP_IA_MODELS = [
  { value: "gpt-4o-mini", label: "gpt-4o-mini" },
  { value: "gpt-4o", label: "gpt-4o" },
  { value: "gpt-4.1-mini", label: "gpt-4.1-mini" },
];

export const ISP_IA_DEFAULTS = {
  model: "gpt-4o-mini",
  maxTurns: 30,
};

const isValidUrl = (value) => {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (e) {
    return false;
  }
};

const asTrimmedString = (value) => {
  if (value === undefined || value === null) return "";
  return String(value).trim();
};

export const normalizeSgpMode = (mode) => {
  const value = asTrimmedString(mode).toLowerCase();
  return SGP_MODES.some((opt) => opt.value === value) ? value : SGP_DEFAULT_MODE;
};

// true when both URA credentials are filled (what "auto" needs to use the URA API)
export const hasSgpUraCredentials = (values) =>
  Boolean(asTrimmedString(values && values.sgpApp) && asTrimmedString(values && values.sgpToken));

const parseRawJson = (jsonContent) => {
  let raw = jsonContent;
  if (typeof raw === "string") {
    if (!raw.trim()) return {};
    try {
      raw = JSON.parse(raw);
    } catch (e) {
      return {};
    }
  }
  return raw && typeof raw === "object" ? raw : {};
};

// jsonContent (string or object) -> SGP form values. Missing keys get form-safe
// defaults so old integrations (without the URA keys) load normally.
export const parseSgpJsonContent = (jsonContent) => {
  const raw = parseRawJson(jsonContent);
  const pick = (key, fallback = "") =>
    raw[key] === undefined || raw[key] === null ? fallback : raw[key];
  return {
    sgpIeSenha: pick("sgpIeSenha"),
    sgpUrl: pick("sgpUrl"),
    sgpWidePayToken: pick("sgpWidePayToken"),
    tipoIntegracao: pick("tipoIntegracao", "NA") || "NA",
    sgpApp: pick("sgpApp"),
    sgpToken: pick("sgpToken"),
    sgpMode: normalizeSgpMode(pick("sgpMode", SGP_DEFAULT_MODE)),
  };
};

// SGP form values -> jsonContent object. Empty URA fields are omitted and the
// default mode ("auto") is not written, so integrations that never used the
// URA keep a jsonContent identical to before.
export const buildSgpJsonContent = (values, baseJson = {}) => {
  const json = {
    ...parseRawJson(baseJson),
    sgpIeSenha: values.sgpIeSenha,
    sgpUrl: asTrimmedString(values.sgpUrl),
    sgpWidePayToken: values.sgpWidePayToken,
    tipoIntegracao: values.tipoIntegracao || "NA",
  };

  const sgpApp = asTrimmedString(values.sgpApp);
  const sgpToken = asTrimmedString(values.sgpToken);
  if (sgpApp) json.sgpApp = sgpApp;
  else delete json.sgpApp;
  if (sgpToken) json.sgpToken = sgpToken;
  else delete json.sgpToken;

  const sgpMode = normalizeSgpMode(values.sgpMode);
  if (sgpMode !== SGP_DEFAULT_MODE) json.sgpMode = sgpMode;
  else delete json.sgpMode;

  return json;
};

export const validateSgpConfig = (values) => {
  const errors = [];
  if (!values.name) errors.push(i18n.t("erp.validation.nameRequired"));
  if (!values.sgpUrl) errors.push(i18n.t("erp.validation.sgpUrlRequired"));
  else if (!isValidUrl(values.sgpUrl)) errors.push(i18n.t("erp.validation.urlInvalid"));

  const sgpApp = asTrimmedString(values.sgpApp);
  const sgpToken = asTrimmedString(values.sgpToken);
  const rawMode = asTrimmedString(values.sgpMode);
  const sgpMode = normalizeSgpMode(rawMode);
  if (rawMode && rawMode.toLowerCase() !== sgpMode) {
    errors.push(i18n.t("erp.validation.sgpModeInvalid"));
  }
  if (sgpMode === "ura") {
    if (!sgpApp) errors.push(i18n.t("erp.validation.sgpAppRequired"));
    if (!sgpToken) errors.push(i18n.t("erp.validation.sgpTokenRequired"));
  } else if ((sgpApp && !sgpToken) || (!sgpApp && sgpToken)) {
    // In auto/central, App and Token only make sense together.
    errors.push(i18n.t("erp.validation.sgpUraPairRequired"));
  }
  return errors;
};

export const validateIxcConfig = (values) => {
  const errors = [];
  if (!values.name) errors.push(i18n.t("erp.validation.nameRequired"));
  if (!values.ixcUrl) errors.push(i18n.t("erp.validation.ixcUrlRequired"));
  else if (!isValidUrl(values.ixcUrl)) errors.push(i18n.t("erp.validation.urlInvalid"));
  if (!values.ixcToken) errors.push(i18n.t("erp.validation.ixcTokenRequired"));
  if (values.timeoutMs !== "" && values.timeoutMs !== undefined && values.timeoutMs !== null) {
    const n = Number(values.timeoutMs);
    if (!Number.isFinite(n) || n <= 0) errors.push(i18n.t("erp.validation.timeoutInvalid"));
  }
  return errors;
};

export const validateAsaasConfig = (values) => {
  const errors = [];
  if (!values.name) errors.push(i18n.t("erp.validation.nameRequired"));
  if (!values.asaasApiKey) errors.push(i18n.t("erp.validation.asaasApiKeyRequired"));
  if (values.asaasEnvironment && !["production", "sandbox"].includes(values.asaasEnvironment)) {
    errors.push(i18n.t("erp.validation.asaasEnvironmentInvalid"));
  }
  if (values.timeoutMs !== "" && values.timeoutMs !== undefined && values.timeoutMs !== null) {
    const n = Number(values.timeoutMs);
    if (!Number.isFinite(n) || n <= 0) errors.push(i18n.t("erp.validation.timeoutInvalid"));
  }
  return errors;
};

export const validateIspIaConfig = (values) => {
  const errors = [];
  if (!values.name) errors.push(i18n.t("erp.validation.nameRequired"));
  if (!values.erpIntegrationId) errors.push(i18n.t("erp.validation.erpIntegrationRequired"));
  if (!values.openaiApiKey) errors.push(i18n.t("erp.validation.openaiApiKeyRequired"));
  if (values.maxTurns !== "" && values.maxTurns !== undefined && values.maxTurns !== null) {
    const n = Number(values.maxTurns);
    if (!Number.isInteger(n) || n <= 0) errors.push(i18n.t("erp.validation.maxTurnsInvalid"));
  }
  return errors;
};

// Hospedagem — conector com o site (H1) e agente de IA (H2).
export const HOSPEDAGEM_PROVIDERS = [
  { value: "alugue_no_litoral", label: "Alugue no Litoral (site)" },
];

export const HOSPEDAGEM_IA_DEFAULTS = {
  model: "gpt-4o-mini",
  maxTurns: 30,
  voice: "alloy",
  voiceMode: "mirror",
};

export const validateHospedagemConectorConfig = (values) => {
  const errors = [];
  if (!values.name) errors.push(i18n.t("erp.validation.nameRequired"));
  if (
    !values.provider ||
    !HOSPEDAGEM_PROVIDERS.some((opt) => opt.value === values.provider)
  ) {
    errors.push(i18n.t("erp.validation.hospedagemProviderRequired"));
  }
  if (!values.baseUrl) errors.push(i18n.t("erp.validation.hospedagemBaseUrlRequired"));
  else if (!isValidUrl(values.baseUrl)) errors.push(i18n.t("erp.validation.urlInvalid"));
  if (!values.apiKey) errors.push(i18n.t("erp.validation.hospedagemApiKeyRequired"));
  return errors;
};

// openaiApiKey is intentionally NOT required — empty means "use the platform key".
export const validateHospedagemIaConfig = (values) => {
  const errors = [];
  if (!values.name) errors.push(i18n.t("erp.validation.nameRequired"));
  if (!values.hospedagemIntegrationId) {
    errors.push(i18n.t("erp.validation.hospedagemIntegrationRequired"));
  }
  if (!values.nomeAgente) errors.push(i18n.t("erp.validation.hospedagemNomeAgenteRequired"));
  if (!values.nomeEmpresa) errors.push(i18n.t("erp.validation.hospedagemNomeEmpresaRequired"));
  if (!values.persona) errors.push(i18n.t("erp.validation.hospedagemPersonaRequired"));
  if (values.maxTurns !== "" && values.maxTurns !== undefined && values.maxTurns !== null) {
    const n = Number(values.maxTurns);
    if (!Number.isInteger(n) || n <= 0) errors.push(i18n.t("erp.validation.maxTurnsInvalid"));
  }
  return errors;
};
